<template>
  <section class="rounded" :class="$style.container">
    <div class="d-flex justify-space-between">
      <h3 class="mb-2" :class="$style.header">
        {{ $t('filter.sparePartsFinder.boxTitle') }}
      </h3>
      <div>
        <v-dialog v-model="showSkuModal" transition="dialog-transition" max-width="60rem">
          <template #activator="{ on, attrs }">
            <v-btn icon small class="primary--text" v-bind="attrs" v-on="on"
              ><v-icon dark>mdi-information-outline</v-icon></v-btn
            >
          </template>
          <v-card>
            <v-card-title class="d-flex align-center">
              {{ $t('filter.sparePartsFinder.infoModalTitle') }}
              <v-spacer></v-spacer>
              <v-btn icon :title="$t('general.close')" @click="showSkuModal = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <p><BaseRichtext :html="$t('filter.sparePartsFinder.infoModalText')" /></p>
              <ComplaintFormTutorialInfoBox tutorial-translation-type="components" />
            </v-card-text>
          </v-card>
        </v-dialog>
      </div>
    </div>

    <p class="mb-2" :class="$style.subheader">
      {{ $t('filter.sparePartsFinder.boxSubtitle') }}
    </p>
    <portal
      :to="showSerialNumberBox ? `${identifier}-primarySearch` : `${identifier}-secondarySearch`"
    >
      <SparePartsSearchBar :class="$style.searchinput" :serial-number="bikeSerialNumber" />
    </portal>
    <portal
      :to="showSerialNumberBox ? `${identifier}-secondarySearch` : `${identifier}-primarySearch`"
    >
      <div>
        <p class="mb-2" :class="$style.subheader">
          {{ $t('filter.sparePartsFinder.boxSubtitleSku') }}
        </p>
        <v-text-field
          v-model="skuNumber"
          :class="$style.searchinput"
          :label="$t('filter.sparePartsFinder.boxSkuInputLabel')"
          outlined
          dense
          :hide-details="!hasSkuNumberError"
          autocomplete="off"
          :error-messages="
            hasSkuNumberError ? $t('filter.sparePartsFinder.boxSkuInputErrormessage') : undefined
          "
          @keyup.enter="searchBySkuNumber"
        >
          <template #append>
            <v-btn
              v-show="skuNumber"
              icon
              small
              :height="36"
              :width="36"
              :elevation="0"
              class="mr-3"
              @click="clearSkuNumber"
            >
              <v-icon medium>mdi-close-circle</v-icon>
            </v-btn>
            <v-btn
              color="primary"
              :disabled="!searchBySkuNumberAllowed"
              icon
              small
              :height="36"
              :width="36"
              :elevation="0"
              @click="searchBySkuNumber"
            >
              <v-icon large>mdi-magnify</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </div>
    </portal>

    <portal-target :name="`${identifier}-primarySearch`" />

    <p class="mt-3 mb-0">
      <v-btn
        class="primary--text"
        text
        x-small
        @click="showSecondarySearch = !showSecondarySearch"
        >{{
          $t(
            showSerialNumberBox
              ? 'filter.sparePartsFinder.boxSkuButtonText'
              : 'filter.sparePartsFinder.boxSerialNumberButtonText',
          )
        }}</v-btn
      >
    </p>
    <portal-target
      v-show="showSecondarySearch"
      class="mt-3"
      :name="`${identifier}-secondarySearch`"
    >
    </portal-target>
    <p v-show="hasRole(ROLES.SHOP_ARCHIVE_R.p)" class="mt-3 mb-0">
      <BaseIconLink :class="$style.link" type="link" :href="archiveRoute" :route="true">
        {{ $t('filter.sparePartsFinder.archiveLinkText') }}
      </BaseIconLink>
    </p>
  </section>
</template>

<script>
import { get, isUndefined } from 'lodash-es'
import { v1 as uuidv1 } from 'uuid'
import { matchSku } from '@/js/helper/sku-patterns.js'
import FILTER_CONSTANTS from '@/js/helper/filter-constants.js'
import ComplaintFormTutorialInfoBox from '@/js/components/Forms/ComplaintForm/ComplaintFormTutorialInfoBox.vue'
import SparePartsSearchBar from '@/js/components/Shared/SparePartsSearchBar.vue'

// import { mapActions, mapGetters } from 'vuex'
import { mapGetters } from 'vuex'

import { types as STOREMODULES } from '@/js/plugins/store/store-modules.js'

// import APP_ACTIONS from '@/js/plugins/store/modules/app/action-types.js'
import APP_GETTER from '@/js/plugins/store/modules/app/getter-types.js'
import PermissionMixin from '@/js/mixins/permission-mixin.js'

export default {
  name: 'SparePartsFinder',
  components: {
    ComplaintFormTutorialInfoBox,
    SparePartsSearchBar,
  },
  mixins: [PermissionMixin],
  props: {
    identifier: {
      type: String,
      require: true,
      default: () => uuidv1(),
    },
    serialNumber: {
      type: String,
      default: undefined,
    },
    sku: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      showSerialNumberBox: !this.sku || !!this.serialNumber,
      bikeSerialNumber: this.serialNumber,
      skuNumber: this.sku,
      showSkuModal: false,
      showSecondarySearch: false,
    }
  },
  computed: {
    archiveRoute() {
      return {
        name: FILTER_CONSTANTS.SCOPE.ARCHIVE,
      }
    },
    currentRouteSku() {
      return get(this.$route, ['params', 'sku'])
    },
    searchBySkuNumberAllowed() {
      return (
        !(isUndefined(this.skuNumber) || this.skuNumber.length < 5 || this.hasSkuNumberError) &&
        this.currentRouteSku !== this.skuNumber
      )
    },
    hasBikeSerialNumberError() {
      if (isUndefined(this.bikeSerialNumber)) {
        return false
      }
      return false
      // return /\D/.test(this.bikeSerialNumber)
    },
    hasSkuNumberError() {
      if (isUndefined(this.skuNumber)) {
        return false
      }
      return !matchSku(this.skuNumber)
    },
    searchByBikeSerialNumberLink() {
      return this.searchByBikeSerialNumberAllowed
        ? {
            name: `${FILTER_CONSTANTS.SCOPE.SERVICE}-sn`,
            params: {
              serialNumber: this.bikeSerialNumber,
              listLink: get(this.$route, ['params', 'listLink']),
            },
          }
        : undefined
    },
    searchBySkuNumberLink() {
      return this.searchBySkuNumberAllowed
        ? {
            name: `${FILTER_CONSTANTS.SCOPE.SERVICE}-sku`,
            params: {
              sku: this.skuNumber,
              listLink: get(this.$route, ['params', 'listLink']),
            },
          }
        : undefined
    },
    ...mapGetters(STOREMODULES.APP, {
      isSmWidth: APP_GETTER.IS_SM_WIDTH,
    }),
  },

  methods: {
    searchBySkuNumber() {
      if (!this.searchBySkuNumberAllowed) {
        return
      }
      const link = this.searchBySkuNumberLink
      this.clearSkuNumber()
      this.$router.push(link)
    },
    clearSkuNumber() {
      this.skuNumber = undefined
    },
  },
}
</script>
<style lang="scss" module>
.container {
  display: block;
  width: 100%;
  max-width: 395px;
  padding: 0.625rem;

  border: var(--border-grey-lighten-1);
}

.header {
  width: 100%;

  font-size: 1.15rem;
  line-height: 1.25;
}

.searchinput {
  :global(.v-input__slot) {
    padding-right: 0;
  }

  :global(.v-input__append-inner) {
    margin-top: 0.2rem !important;
  }

  :global(a.v-btn) {
    text-decoration: none !important;
  }
}

.link {
  @media screen and (min-width: map-get($grid-breakpoints, 'lg')) {
    margin-top: 2.5rem !important;
  }
  margin-top: 0 !important;
}
</style>
